import React, { useState, useEffect } from 'react';

/**
 * Dialog for configuring legend settings for geography-enriched maps
 * Shows after the map/layer is created and enrichment data is available
 */
const GeographyLegendConfigDialog = ({
  isOpen,
  onClose,
  onApply,
  layerInfo,  // Contains the layer's legendInfo with classBreakInfos
  variableName,
  currentBreakCount = null  // Current number of breaks (auto-detected if not provided)
}) => {
  const [legendTitle, setLegendTitle] = useState('');
  const [numberFormat, setNumberFormat] = useState('number'); // 'number', 'currency', 'percent'
  const [decimalPlaces, setDecimalPlaces] = useState(0);
  const [useCommas, setUseCommas] = useState(true);
  const [numBreaks, setNumBreaks] = useState(null); // null means "Auto"

  // Initialize with variable name and current break count when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (variableName) {
        setLegendTitle(variableName);
      }
      // Initialize break count from current layer or provided count
      const initialBreakCount = currentBreakCount || layerInfo?.classBreakInfos?.length || null;
      setNumBreaks(initialBreakCount);
    }
  }, [isOpen, variableName, currentBreakCount, layerInfo]);

  if (!isOpen) return null;

  // Helper to format numbers for preview
  const formatNumber = (n) => {
    if (n === undefined || n === null || !isFinite(n)) return '...';
    let formatted;
    if (useCommas) {
      formatted = n.toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      });
    } else {
      formatted = n.toFixed(decimalPlaces);
    }

    // Apply format type prefix/suffix
    if (numberFormat === 'currency') {
      return '$' + formatted;
    } else if (numberFormat === 'percent') {
      return formatted + '%';
    }
    return formatted;
  };

  // Default heatmap colors matching mapLayerUtils.js
  const defaultHeatmapColors = [
    [128, 0, 128, 0.35],     // Purple
    [0, 0, 139, 0.20],       // Dark blue
    [135, 206, 235, 0.35],   // Sky blue
    [144, 238, 144, 0.35],   // Light green
    [255, 255, 144, 0.35],   // Light yellow
    [255, 165, 0, 0.15],     // Orange
    [255, 99, 71, 0.15],     // Tomato red
  ];

  // Helper to extract color from ArcGIS Color object or array
  const extractColor = (colorObj) => {
    if (!colorObj) return [128, 128, 128, 0.5];

    // If it's already an array
    if (Array.isArray(colorObj)) {
      return colorObj;
    }

    // ArcGIS Color object - try toRgba() method first
    if (typeof colorObj.toRgba === 'function') {
      const rgba = colorObj.toRgba();
      return [rgba[0], rgba[1], rgba[2], rgba[3]];
    }

    // ArcGIS Color object with r, g, b, a properties
    if (colorObj.r !== undefined) {
      return [colorObj.r, colorObj.g, colorObj.b, colorObj.a !== undefined ? colorObj.a : 1];
    }

    // Try accessing as array indices (some Color objects support this)
    if (colorObj[0] !== undefined) {
      return [colorObj[0], colorObj[1], colorObj[2], colorObj[3] !== undefined ? colorObj[3] : 1];
    }

    return [128, 128, 128, 0.5];
  };

  // Color scheme for different break counts (maps to purple-to-red heat map palette)
  const BREAK_COUNT_TO_COLORS = {
    1: [[128, 0, 128, 0.35]],
    2: [[128, 0, 128, 0.35], [255, 99, 71, 0.35]],
    3: [[128, 0, 128, 0.35], [144, 238, 144, 0.35], [255, 99, 71, 0.35]],
    4: [[128, 0, 128, 0.35], [135, 206, 235, 0.35], [255, 255, 144, 0.35], [255, 99, 71, 0.35]],
    5: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [144, 238, 144, 0.35], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
    6: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [135, 206, 235, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
    7: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [135, 206, 235, 0.35], [144, 238, 144, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
    8: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [135, 206, 235, 0.35], [144, 238, 144, 0.35], [144, 238, 144, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
    9: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [135, 206, 235, 0.35], [135, 206, 235, 0.35], [144, 238, 144, 0.35], [255, 255, 144, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
    10: [[128, 0, 128, 0.35], [0, 0, 139, 0.20], [0, 0, 139, 0.20], [135, 206, 235, 0.35], [144, 238, 144, 0.35], [144, 238, 144, 0.35], [255, 255, 144, 0.35], [255, 165, 0, 0.20], [255, 165, 0, 0.20], [255, 99, 71, 0.35]],
  };

  // Generate preview labels from classBreakInfos or estimated breaks based on selected numBreaks
  const getPreviewLabels = () => {
    const currentBreakCount = layerInfo?.classBreakInfos?.length || 7;
    const targetBreakCount = numBreaks || currentBreakCount;
    const breaksWillChange = numBreaks !== null && numBreaks !== currentBreakCount;

    // If breaks will change, show estimated preview based on data range
    if (breaksWillChange && layerInfo?.classBreakInfos?.length > 0) {
      // Get the data range from existing breaks by finding all valid values
      const existingBreaks = layerInfo.classBreakInfos;

      // Collect all numeric values from breaks (minValue and maxValue)
      const allValues = [];
      existingBreaks.forEach(b => {
        if (typeof b.minValue === 'number' && isFinite(b.minValue)) allValues.push(b.minValue);
        if (typeof b.maxValue === 'number' && isFinite(b.maxValue)) allValues.push(b.maxValue);
      });

      // If we found values, use them; otherwise use defaults
      let minValue = 0;
      let maxValue = 100000;

      if (allValues.length > 0) {
        minValue = Math.min(...allValues);
        maxValue = Math.max(...allValues);
      }

      // Ensure we have a valid range
      if (maxValue <= minValue) {
        maxValue = minValue + 100000;
      }

      // Generate evenly distributed preview breaks
      const range = maxValue - minValue;
      const interval = range / targetBreakCount;
      const colors = BREAK_COUNT_TO_COLORS[targetBreakCount] || BREAK_COUNT_TO_COLORS[7];

      const previewBreaks = [];
      for (let i = 0; i < targetBreakCount; i++) {
        const breakMin = minValue + (i * interval);
        const breakMax = minValue + ((i + 1) * interval);

        let label;
        if (i === 0) {
          label = `Less than ${formatNumber(Math.round(breakMax))}`;
        } else if (i === targetBreakCount - 1) {
          label = `${formatNumber(Math.round(breakMin))} or More`;
        } else {
          label = `${formatNumber(Math.round(breakMin))} - ${formatNumber(Math.round(breakMax))}`;
        }

        previewBreaks.push({
          color: colors[i] || [128, 128, 128, 0.5],
          label: label
        });
      }

      return previewBreaks;
    }

    // Show current breaks if no change or no layer info
    if (!layerInfo?.classBreakInfos || layerInfo.classBreakInfos.length === 0) {
      // Show placeholder preview with example values
      const colors = BREAK_COUNT_TO_COLORS[targetBreakCount] || BREAK_COUNT_TO_COLORS[7];
      const previewBreaks = [];
      for (let i = 0; i < targetBreakCount; i++) {
        const baseValue = 1000 * (i + 1);
        let label;
        if (i === 0) {
          label = `Less than ${formatNumber(baseValue)}`;
        } else if (i === targetBreakCount - 1) {
          label = `${formatNumber(baseValue - 1000)} or More`;
        } else {
          label = `${formatNumber(baseValue - 1000)} - ${formatNumber(baseValue)}`;
        }
        previewBreaks.push({
          color: colors[i] || [128, 128, 128, 0.5],
          label: label
        });
      }
      return previewBreaks;
    }

    const breaks = layerInfo.classBreakInfos;
    const breakCount = breaks.length;

    return breaks.map((breakInfo, index) => {
      let label;
      if (index === 0) {
        label = `Less than ${formatNumber(breakInfo.maxValue)}`;
      } else if (index === breakCount - 1) {
        const prevMax = breaks[index - 1]?.maxValue || 0;
        label = `${formatNumber(prevMax)} or More`;
      } else {
        const prevMax = breaks[index - 1]?.maxValue || 0;
        label = `${formatNumber(prevMax)} - ${formatNumber(breakInfo.maxValue)}`;
      }

      // Get color from symbol, breakInfo.color, or fall back to default colors
      let color = defaultHeatmapColors[index] || [128, 128, 128, 0.5];

      if (breakInfo.symbol?.color) {
        color = extractColor(breakInfo.symbol.color);
      } else if (breakInfo.color) {
        color = extractColor(breakInfo.color);
      }

      return { color, label };
    });
  };

  const handleApply = () => {
    const currentBreaks = layerInfo?.classBreakInfos?.length || 7;
    const breaksChanged = numBreaks !== null && numBreaks !== currentBreaks;

    console.log("[GeographyLegendConfigDialog] Apply clicked:", {
      legendTitle,
      numberFormat,
      decimalPlaces,
      useCommas,
      numBreaks,
      currentBreaks,
      breaksChanged,
      hasOnApply: typeof onApply === 'function'
    });

    if (typeof onApply === 'function') {
      onApply({
        legendTitle,
        numberFormat,
        decimalPlaces,
        useCommas,
        numBreaks: numBreaks,  // null means auto, otherwise specific count
        breaksChanged: breaksChanged  // Flag to indicate if regeneration is needed
      });
    } else {
      console.error("[GeographyLegendConfigDialog] onApply is not a function:", onApply);
    }
  };

  const previewLabels = getPreviewLabels();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Configure Legend
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Customize how the legend appears for this map
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]">
            {/* Legend Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Legend Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={legendTitle}
                onChange={(e) => setLegendTitle(e.target.value)}
                placeholder="Enter legend title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Title displayed at the top of the map legend</p>
            </div>

            {/* Number of Breaks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Breaks
              </label>
              <select
                value={numBreaks || ''}
                onChange={(e) => setNumBreaks(e.target.value === '' ? null : parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Auto (based on data)</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the number of color categories. Changing this will regenerate the map legend with new value ranges.
              </p>
            </div>

            {/* Number Format Options - 3 columns */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Format
                </label>
                <select
                  value={numberFormat}
                  onChange={(e) => setNumberFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="number">Number</option>
                  <option value="currency">Currency ($)</option>
                  <option value="percent">Percent (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Decimals
                </label>
                <select
                  value={decimalPlaces}
                  onChange={(e) => setDecimalPlaces(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={0}>0</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Commas
                </label>
                <label className="flex items-center h-[42px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCommas}
                    onChange={(e) => setUseCommas(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use</span>
                </label>
              </div>
            </div>

            {/* Legend Preview */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Preview
                </label>
                {numBreaks !== null && numBreaks !== (layerInfo?.classBreakInfos?.length || 7) && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                    * Estimated - actual values calculated on Apply
                  </span>
                )}
              </div>
              <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/50">
                <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-3">
                  <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                    {legendTitle || 'Legend Title'}
                  </div>
                  <div className="space-y-1.5">
                    {previewLabels.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div
                          style={{
                            width: '24px',
                            height: '14px',
                            backgroundColor: `rgba(${item.color[0]}, ${item.color[1]}, ${item.color[2]}, ${item.color[3]})`,
                            border: '1px solid rgba(0,0,0,0.2)',
                            flexShrink: 0,
                            borderRadius: '2px'
                          }}
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md"
            >
              Skip
            </button>
            <button
              onClick={handleApply}
              disabled={!legendTitle.trim()}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm ${
                !legendTitle.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeographyLegendConfigDialog;
